import React, { useEffect, useRef } from "react";
import { AppleGlyph, PlayGlyph, ArrowGlyph } from "./glyphs";

function MagneticCard({ children, className, ...rest }) {
  const ref = useRef(null);
  const glowRef = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    if (glowRef.current) {
      glowRef.current.style.left = (e.clientX - r.left) + "px";
      glowRef.current.style.top  = (e.clientY - r.top)  + "px";
    }
  };
  return (
    <div ref={ref} className={className} onMouseMove={onMove} {...rest}>
      <div className="glow" ref={glowRef} />
      {children}
    </div>
  );
}

export function Marquee() {
  const items = ["Verified members", "Real people", "Safe & private", "Mutual matches", "Built to last"];
  const row = (
    <span>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {it} <span className="heart">&#9829;</span>
        </React.Fragment>
      ))}
    </span>
  );
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">{row}{row}{row}{row}</div>
    </div>
  );
}

function OrbitalFeatures() {
  useEffect(() => {
    const chips = document.querySelectorAll(".orbit-chip");
    let raf, t = 0;
    const tick = () => {
      t += 0.2;
      chips.forEach((c) => {
        const dir = c.dataset.ring === "a" ? -1 : 1;
        const speed = c.dataset.ring === "a" ? 0.1 : 0.07;
        c.style.transform = c.dataset.transform + ` rotate(${dir * t * speed}deg)`;
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  const chipsA = [
    { label: "Personality match", ico: "✦", angle: 0 },
    { label: "Verified ID",       ico: "✓", angle: 72 },
    { label: "Voice prompts",     ico: "♪", angle: 144 },
    { label: "Daily picks",       ico: "✿", angle: 216 },
    { label: "Block & report",    ico: "⛨", angle: 288 },
  ];
  const chipsB = [
    { label: "Love letters",   ico: "✉", angle: 36  },
    { label: "Smart filters",  ico: "≡", angle: 108 },
    { label: "Vibe check",     ico: "♥", angle: 180 },
    { label: "Read receipts",  ico: "◉", angle: 252 },
    { label: "Privacy fuzz",   ico: "✦", angle: 324 },
  ];

  return (
    <div className="features-orbit">
      <div className="orbit-ring r2">
        {chipsB.map((c) => {
          const r = "min(36vw, 440px)";
          const transform = `translate(-50%, -50%) rotate(${c.angle}deg) translateY(calc(-1 * ${r})) rotate(${-c.angle}deg)`;
          return (
            <div key={c.label}
                 className="orbit-chip"
                 data-ring="b"
                 data-transform={transform}
                 style={{ top: "50%", left: "50%", transform }}>
              <span className="ico">{c.ico}</span>{c.label}
            </div>
          );
        })}
      </div>
      <div className="orbit-ring">
        {chipsA.map((c) => {
          const r = "min(28vw, 340px)";
          const transform = `translate(-50%, -50%) rotate(${c.angle}deg) translateY(calc(-1 * ${r})) rotate(${-c.angle}deg)`;
          return (
            <div key={c.label}
                 className="orbit-chip"
                 data-ring="a"
                 data-transform={transform}
                 style={{ top: "50%", left: "50%", transform }}>
              <span className="ico">{c.ico}</span>{c.label}
            </div>
          );
        })}
      </div>
      <div className="orbit-core">
        Built around<br /><em>you</em>
      </div>
    </div>
  );
}

export function Features() {
  const quizRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const fills = e.target.querySelectorAll(".fill");
          fills.forEach((f, i) => {
            const pct = parseFloat(f.dataset.pct) / 100;
            setTimeout(() => { f.style.transform = `scaleX(${pct})`; }, i * 120);
          });
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });
    if (quizRef.current) obs.observe(quizRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="section" id="features">
      <div className="section-head reveal">
        <div>
          <div className="section-eyebrow">02 — How we're different</div>
          <h2 className="section-title">A full toolkit for<br /><em>finding the one</em>.</h2>
        </div>
        <p className="section-desc">
          Most apps are built to keep you swiping. We're built to help you stop.
          Five core systems work together to surface real compatibility, fast.
        </p>
      </div>

      <OrbitalFeatures />

      <div className="bento" style={{ marginTop: 40 }}>
        <MagneticCard className="bento-card span-6 reveal">
          <div>
            <div className="bento-num">01 / Compatibility</div>
            <h3 className="bento-title">A quiz that means something.</h3>
            <p className="bento-desc">
              48 questions calibrated by therapists. We surface matches you'd
              actually click with — not just look at.
            </p>
          </div>
          <div className="bento-quiz" ref={quizRef}>
            {[
              ["Sense of humour", 92],
              ["Life pace",       88],
              ["Family values",   81],
              ["Adventure",       95],
            ].map(([k, v]) => (
              <div className="quiz-bar" key={k}>
                <span className="fill" data-pct={v} style={{ transform: "scaleX(0)" }} />
                <span>{k}</span>
                <span className="pct">{v}%</span>
              </div>
            ))}
          </div>
        </MagneticCard>

        <MagneticCard className="bento-card span-6 blue reveal">
          <div>
            <div className="bento-num">02 / Smart matching</div>
            <h3 className="bento-title">Mutual interest only.</h3>
            <p className="bento-desc">
              We only show you to people likely to like you back. Less rejection,
              more conversations that go somewhere.
            </p>
          </div>
          <div className="bento-cards-fan">
            <div className="fan-card">
              <div className="fan-name">Anjali, 25</div>
              <div className="fan-meta">Kochi · Designer</div>
            </div>
            <div className="fan-card">
              <div className="fan-name">Maya, 24</div>
              <div className="fan-meta">Mumbai · Painter</div>
            </div>
            <div className="fan-card">
              <div className="fan-name">Iris, 26</div>
              <div className="fan-meta">Delhi · Architect</div>
            </div>
          </div>
        </MagneticCard>

        <MagneticCard className="bento-card span-4 reveal">
          <div>
            <div className="bento-num">03 / Love letters</div>
            <h3 className="bento-title">Send a love letter.</h3>
            <p className="bento-desc">
              Stand out with a long-form note, not a wave. One per week — make it count.
            </p>
          </div>
          <div style={{ marginTop: 18, padding: 16, background: "var(--bg-2)", borderRadius: 14, fontFamily: "var(--serif)", fontStyle: "italic", fontSize: 17, lineHeight: 1.4 }}>
            "Your bookshelf made me smile. The Calvino, the Tokarczuk, the dog-eared Didion…"
          </div>
        </MagneticCard>

        <MagneticCard className="bento-card span-4 dark reveal">
          <div>
            <div className="bento-num" style={{ opacity: 0.6 }}>04 / Chat</div>
            <h3 className="bento-title">Conversations, not pings.</h3>
          </div>
          <div className="bento-chat">
            <div className="bubble">Have you tried that biryani place on MG Road?</div>
            <div className="bubble me">Their dum biryani changed my life.</div>
            <div className="bubble typing"><span/><span/><span/></div>
          </div>
        </MagneticCard>

        <MagneticCard className="bento-card span-4 reveal">
          <div>
            <div className="bento-num">05 / Verified</div>
            <h3 className="bento-title">Every profile, double-checked.</h3>
            <p className="bento-desc">
              Selfie + ID verification. No bots, no catfish, no surprises.
            </p>
          </div>
          <div className="bento-shield">
            <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M50 8L20 18v28c0 18 13 33 30 38 17-5 30-20 30-38V18L50 8z"/>
              <path d="M38 50l8 8 16-16" strokeWidth="3" stroke="var(--blue-deep)"/>
            </svg>
          </div>
        </MagneticCard>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section className="section" id="how" style={{ paddingTop: 40 }}>
      <div className="section-head reveal">
        <div>
          <div className="section-eyebrow">03 — How it works</div>
          <h2 className="section-title">Three steps to<br /><em>somebody real</em>.</h2>
        </div>
      </div>
      <div className="steps reveal">
        <div className="step">
          <div className="step-num">01</div>
          <div className="step-title">Take the quiz</div>
          <div className="step-desc">Six minutes. Honest answers. We do the rest.</div>
        </div>
        <div className="step">
          <div className="step-num">02</div>
          <div className="step-title">Meet your daily picks</div>
          <div className="step-desc">Five hand-curated matches every morning at 9am.</div>
        </div>
        <div className="step">
          <div className="step-num">03</div>
          <div className="step-title">Start a real conversation</div>
          <div className="step-desc">Voice prompts, photo prompts, love letters — never just "hey".</div>
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const stories = [
    {
      img: "https://images.pexels.com/photos/2253879/pexels-photo-2253879.jpeg?auto=compress&cs=tinysrgb&w=600",
      quote: "The matches actually felt thoughtful — not a lottery. We met for coffee, then lunch, then everything.",
      avatar: "A",
      names: "Anjali & Daniel",
      meta: "Met March 2025 · Kochi",
    },
    {
      img: "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=600",
      quote: "I deleted three other apps the day I downloaded this one. The vibe is just calmer.",
      avatar: "M",
      names: "Maya & Theo",
      meta: "Met July 2025 · Mumbai",
    },
    {
      img: "https://images.pexels.com/photos/1701202/pexels-photo-1701202.jpeg?auto=compress&cs=tinysrgb&w=600",
      quote: "The love letter feature is genius. It got me writing instead of reacting. He wrote back the next morning.",
      avatar: "I",
      names: "Iris & Arjun",
      meta: "Met Jan 2026 · Bengaluru",
    },
  ];
  return (
    <section className="section testimonials" id="stories">
      <div className="section-head reveal">
        <div>
          <div className="section-eyebrow">04 — Real stories</div>
          <h2 className="section-title">People who<br /><em>found theirs</em>.</h2>
        </div>
        <p className="section-desc">
          We don't celebrate engagement metrics. We celebrate the moment you
          delete the app — for the right reason.
        </p>
      </div>
      <div className="testi-grid reveal">
        {stories.map((s) => (
          <div className="testi" key={s.names}>
            <div style={{ width: "100%", height: 160, borderRadius: 14, backgroundImage: `url(${s.img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <blockquote>{s.quote}</blockquote>
            <div className="testi-author">
              <div className="testi-avatar">{s.avatar}</div>
              <div>
                <div style={{ color: "var(--ink)", fontWeight: 500 }}>{s.names}</div>
                <div>{s.meta}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function About() {
  return (
    <section className="section" id="about" style={{ paddingTop: 40 }}>
      <div className="section-head reveal">
        <div>
          <div className="section-eyebrow">05 — About</div>
          <h2 className="section-title">A small team.<br />A <em>big</em> idea.</h2>
        </div>
      </div>
      <div className="about">
        <div className="about-copy reveal" data-cursor="text">
          We were tired of dating apps designed like slot machines. Of swiping
          for hours and ending up nowhere. So we built <em>Hridhayam</em> —
          a quieter, kinder corner of the internet where finding someone real
          is the whole point, not the side effect.
        </div>
        <aside>
          <div className="about-points">
            {[
              ["01", "Verified, every single one", "Selfie + ID checks before any profile goes live."],
              ["02", "Mutual matches only",        "We only show you to people likely to match back."],
              ["03", "Compatibility-first",        "Therapist-built quiz. Real chemistry science."],
              ["04", "Private by default",         "Block-screenshot, location fuzz, zero data sales."],
            ].map(([n, t, d]) => (
              <div className="about-point reveal" key={n}>
                <div className="about-point-num">{n}</div>
                <div>
                  <div className="about-point-title">{t}</div>
                  <div className="about-point-desc">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

const APP_STORE_URL  = "https://apps.apple.com/us/app/hridhayam/id6444903823";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.malayali_datingapp&hl=en&gl=US";

export function CTA() {
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const p = 1 - Math.max(0, Math.min(1, r.top / window.innerHeight));
      const h1 = ref.current.querySelector(".cta-heart.h1");
      const h2 = ref.current.querySelector(".cta-heart.h2");
      if (h1) h1.style.transform = `translate(${p * 30}px, ${p * -40}px) rotate(${p * 8}deg)`;
      if (h2) h2.style.transform = `translate(${p * -30}px, ${p * 40}px) rotate(${180 + p * -8}deg)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <section className="cta" id="download" ref={ref}>
      <div className="cta-bg">
        <div className="cta-heart h1">&#9829;</div>
        <div className="cta-heart h2">&#9829;</div>
      </div>
      <div className="cta-eyebrow">06 — Download</div>
      <h2 className="cta-title" data-cursor="text">
        Love is just a<br /><em>conversation</em> away.
      </h2>
      <div className="cta-buttons">
        <a className="btn btn-light" href={APP_STORE_URL} target="_blank" rel="noreferrer">
          <AppleGlyph /><span>App Store</span><ArrowGlyph />
        </a>
        <a className="btn btn-outline-light" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
          <PlayGlyph /><span>Play Store</span>
        </a>
      </div>
      <div style={{ marginTop: 32, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".12em", opacity: 0.6, textTransform: "uppercase", position: "relative" }}>
        Available on iOS &amp; Android · Free to download
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div>
          <div className="footer-brand">
            <img src="/h_logo.png" alt="Hridhayam" />
            Hridhayam<span style={{ color: "var(--blue)" }}>.</span>
          </div>
          <p className="footer-tagline">A quieter, kinder corner of the internet for people who want something real.</p>
        </div>
        <div className="footer-col">
          <h4>Product</h4>
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#stories">Stories</a>
          <a href="#download">Download</a>
        </div>
        <div className="footer-col">
          <h4>Legal</h4>
          <a href="/privacy_policy.html">Privacy policy</a>
          <a href="/termsandconditions.html">Terms &amp; conditions</a>
          <a href="#top">Cookies</a>
        </div>
        <div className="footer-col">
          <h4>Support</h4>
          <a href="mailto:hello@hridhayam.app">Contact</a>
          <a href="#top">Help center</a>
          <a href="#top">Press</a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Hridhayam · All rights reserved</span>
        <span>Made with ♥ for people who want something real</span>
      </div>
    </footer>
  );
}
